import { ingestEvents } from '@/atoms/stats'
import { subscribeTimeline } from '@/services/client.service'
import { TFeedSubRequest } from '@/types'
import { loadRelayList } from '@nostr/gadgets/lists'
import * as kinds from '@nostr/tools/kinds'
import { Event } from '@nostr/tools/wasm'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BadgeAward from './Note/BadgeAward'
import NoteOptions from './NoteOptions'
import { FormattedTimestamp } from './FormattedTimestamp'
import { isSmallScreenAtom } from '@/atoms/screenSize'
import { useAtomValue } from 'jotai'
import { toNote } from '@/lib/link'
import { useSecondaryPage } from '@/PageManager'

const LIMIT = 150

/** Lists kind-8 badge awards referencing a kind-30009 badge definition. */
export default function BadgeAwardList({
  event,
  selectedRelays
}: {
  event: Event
  selectedRelays?: string[]
}) {
  const { t } = useTranslation()
  const [subRequests, setSubRequests] = useState<null | TFeedSubRequest[]>(null)
  const [events, setEvents] = useState<Event[]>([])
  const [loading, setLoading] = useState(true)
  const { push } = useSecondaryPage()
  const isSmallScreen = useAtomValue(isSmallScreenAtom)

  const coordinate = useMemo(() => {
    const d = event.tags.find(([name]) => name === 'd')?.[1]
    if (d === undefined) return undefined
    return `${kinds.BadgeDefinition}:${event.pubkey}:${d}`
  }, [event])

  const awards = useMemo(
    () => events.slice().sort((a, b) => b.created_at - a.created_at || a.id.localeCompare(b.id)),
    [events]
  )

  useEffect(() => {
    let isCancelled = false
    ;(async () => {
      if (!coordinate) {
        setSubRequests([])
        return
      }

      const relayUrls =
        selectedRelays ??
        (await loadRelayList(event.pubkey)).items
          .filter((r) => r.write)
          .map((r) => r.url)
          .concat(window.hallway.universe.bigRelayUrls)

      if (isCancelled) return
      setSubRequests([
        {
          source: 'relays',
          urls: relayUrls,
          filter: { '#a': [coordinate], kinds: [kinds.BadgeAward], limit: LIMIT }
        }
      ])
    })()

    return () => {
      isCancelled = true
    }
  }, [event.pubkey, coordinate, selectedRelays])

  useEffect(() => {
    if (!subRequests || subRequests.length === 0) return

    setLoading(true)
    setEvents([])

    const subc = subscribeTimeline(
      subRequests,
      {},
      {
        onEvents: (events, isFinal) => {
          if (isFinal) setLoading(false)
          if (events.length > 0) {
            setEvents(events)
            ingestEvents(events)
          } else if (isFinal) {
            setEvents([])
          }
        },
        onNew: (evt) => {
          ingestEvents([evt])
          setEvents((oldEvents) => {
            if (oldEvents.some((existing) => existing.id === evt.id)) return oldEvents
            return [evt, ...oldEvents].sort((a, b) => b.created_at - a.created_at)
          })
        }
      }
    )

    return () => subc.close()
  }, [subRequests])

  if (!coordinate) {
    return (
      <div className="text-sm py-8 text-center text-muted-foreground">
        {t('This badge has no identifier, awards cannot be looked up.')}
      </div>
    )
  }

  return (
    <div className="min-h-[80vh]">
      <div className="divide-y">
        {awards.map((award) => (
          <div key={award.id} className="px-4 py-3">
            <div className="flex items-center justify-between gap-1 text-sm text-muted-foreground">
              <FormattedTimestamp
                timestamp={event.created_at}
                className="shrink-0"
                short={isSmallScreen}
                href={toNote(award)}
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  push(toNote(award))
                }}
              />
              <NoteOptions event={event} className="py-1 shrink-0 [&_svg]:size-5" />
            </div>
            <BadgeAward event={award} hideDefinition />
          </div>
        ))}
      </div>

      <div className="text-sm mt-2 text-center text-muted-foreground">
        {loading ? t('Loading') : awards.length > 0 ? t('No more awards') : t('No awards yet')}
      </div>
    </div>
  )
}
